import { useFormik } from "formik";
import { FundingForm } from "../types";
import InputField from "./InputField";
import PhoneInput from "./PhoneDropdown";
import { step3Schema } from "./formSchema";
import { AiOutlineLoading } from "react-icons/ai";

interface ContactPersonDetailsProps {
  onSubmit: (values: Partial<FundingForm>) => void;
  onBack: () => void;
  loading: boolean;
  initialData: FundingForm;
  userCountry: string;
  onFieldChange: (field: keyof FundingForm, value: any) => void;
}

const ContactPersonDetails = ({
  onSubmit,
  onBack,
  loading,
  initialData,
  userCountry,
  onFieldChange,
}: ContactPersonDetailsProps) => {
  const formik = useFormik({
    initialValues: {
      firstName: initialData.firstName,
      lastName: initialData.lastName,
      email: initialData.email,
      role: initialData.role,
      countryCode: initialData.countryCode,
      phone: initialData.phone,
      lastUserCountry: initialData.lastUserCountry,
    },
    validationSchema: step3Schema,
    onSubmit: (values) => onSubmit(values),
  });

  const handleChange = (e: React.ChangeEvent<any>) => {
    formik.handleChange(e);
    onFieldChange(e.target.name as keyof FundingForm, e.target.value);
  };

  return (
    <form onSubmit={formik.handleSubmit} className='flex flex-col'>
      <h2 className='text-2xl text-blueShades-4 font-bold pb-2'>
        Contact Person Details
      </h2>
      <p className='text-sm text-ashShade-16'>Let us get to know you</p>
      <div className='flex flex-col gap-6 pt-6'>
        <div className='flex flex-col md:flex-row items-center gap-6'>
          <InputField
            type='string'
            name='firstName'
            labelText='First Name'
            placeholder='e.g John'
            onChange={handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.firstName}
            isRequired
            error={formik.touched.firstName ? formik.errors.firstName : ""}
          />
          <InputField
            type='string'
            name='lastName'
            labelText='Last Name'
            placeholder='e.g Doe'
            onChange={handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.lastName}
            error={formik.touched.lastName ? formik.errors.lastName : ""}
            isRequired
          />
        </div>
        <InputField
          type='email'
          name='email'
          labelText='Email'
          placeholder='e.g johndoe@example.com'
          onChange={handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          error={formik.touched.email ? formik.errors.email : ""}
          isRequired
        />
        <InputField
          type='string'
          name='role'
          labelText='Role'
          placeholder='e.g CEO'
          onChange={handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.role}
          error={formik.touched.role ? formik.errors.role : ""}
          isRequired
        />

        <div className='w-full'>
          <PhoneInput
            onPhoneChange={({ country, phoneNumber }) => {
              if (country) {
                formik.setFieldValue("countryCode", country.dialCode, false);
              }
              formik.setFieldValue("phone", phoneNumber, false);
              onFieldChange("phone", phoneNumber);
              onFieldChange("countryCode", country?.dialCode);
            }}
            value={{
              countryCode: formik.values.countryCode,
              phoneNumber: formik.values.phone,
            }}
            userCountry={userCountry}
            lastUserCountry={formik.values.lastUserCountry}
            onLastUserCountryChange={(country) =>
              formik.setFieldValue("lastUserCountry", country)
            }
            error={formik.touched.phone ? formik.errors.phone : ""}
            labelText='Phone Number'
            isRequired
          />
        </div>
      </div>
      <div className='flex gap-6 items-center pt-6 self-end'>
        <button
          onClick={onBack}
          type='button'
          className='bg-transparent border border-ashShade-19 px-[18px] py-[9px] text-sm text-blueShades-6 rounded-lg'
        >
          Back
        </button>
        <button
          type='submit'
          className='bg-blueShades-6 border border-blueShades-6 px-[18px] py-[9px] text-sm text-white rounded-lg'
        >
          {loading ? (
            <AiOutlineLoading className='text-white animate-spin' />
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </form>
  );
};
export default ContactPersonDetails;
